import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./authprovider";
import protocolsCache from "../utils/protocolsCache";

const offlineContext = createContext();

export function useDataProvider() {
  return useContext(offlineContext);
}

export default function ProvideOfflineData({ children }) {
  const dataProvider = useProvideData();

  return (
    <offlineContext.Provider value={dataProvider}>
      {children}
    </offlineContext.Provider>
  );
}

function useProvideData() {
  const {
    buildings,
    users,
    rajcompanies,
    settings: { fetchSettings },
    devices: { get: getDevices },
    protocols: { getBuildingsGroups, getProtocolFormTemplates, getSchematics, addSchematic, getProtocolSchematicById },
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shortBuildings, setShortBuildings] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [buildingFolders, setBuildingFolders] = useState([]);
  const [protocolTemplates, setProtocolTemplates] = useState([]);
  const [devices, setDevices] = useState([]);
  const [schematics, setSchematics] = useState([]);
  const [settingsData, setSettingsData] = useState({});

  useEffect(() => {
    const fetchStatic = async () => {
      setLoading(true);

      const fetchBuildings = () =>
        new Promise((resolve) => {
          buildings.getShort(({ success, error, content }) => {
            if (success) {
              setShortBuildings(content);
            } else {
              setError(error);
            }
            resolve();
          });
        });

      const fetchWorkers = () =>
        new Promise((resolve) => {
          users.get(({ success, error, content }) => {
            if (success) {
              setWorkers(content);
            } else {
              setError(error);
            }
            resolve();
          }, "protocols");
        });

      const fetchCompanies = () =>
        new Promise((resolve) => {
          rajcompanies.get(null, ({ success, error, content }) => {
            if (success) {
              setCompanies(content);
            } else {
              setError(error);
            }
            resolve();
          });
        });

      const fetchFolders = () =>
        new Promise((resolve) => {
          getBuildingsGroups(({ success, error, content }) => {
            if (success) {
              setBuildingFolders(content);
            } else {
              setError(error);
            }
            resolve();
          });
        });

      const fetchTemplates = () =>
        new Promise((resolve) => {
          getProtocolFormTemplates(({ success, error, content }) => {
            if (success) {
              setProtocolTemplates(content);
            } else {
              setError(error);
            }
            resolve();
          });
        });

      const fetchSchematic = () =>
        new Promise((resolve) => {
          getSchematics(({ success, error, content }) => {
            if (success) {
              setSchematics(content);
            } else {
              setError(error);
            }
            resolve();
          });
        });

      const fetchDevices = () =>
        new Promise((resolve) => {
          getDevices(({ success, error, content }) => {
            if (success) {
              setDevices(content);
            } else {
              setError(error);
            }
            resolve();
          });
        });

      const fetchOfflineSettings = () =>
        new Promise(async (resolve) => {
          const result = await fetchSettings();

          if (result?.success) {
            setSettingsData(result.content || {});
          } else {
            setError(result?.error || "Nie udało się pobrać ustawień.");
          }

          resolve();
        });

      await Promise.all([
        fetchBuildings(),
        fetchWorkers(),
        fetchCompanies(),
        fetchFolders(),
        fetchTemplates(),
        fetchDevices(),
        fetchSchematic(),
        fetchOfflineSettings(),
      ]);

      setLoading(false);
    };

    fetchStatic();
  }, []);

const getProtocolVersionByType = (type) => {
  return protocolTemplates[type]?.version ?? null;
};

const addProtocolSchematic = (file, description) => {
  return new Promise((resolve, reject) => {
    addSchematic(file, description, ({ success, error, content }) => {
      if (success) {
        // aktualizujemy lokalny cache
        setSchematics((prev) => [...prev, content]);
        // zwracamy nowo utworzony schemat do komponentu
        resolve(content);
      } else {
        reject(new Error(error || "Nie udało się dodać schematu"));
      }
    });
  });
};


  return {
    loading,
    error,
    shortBuildings,
    workers,
    companies,
    devices,
    buildingFolders,
    setBuildingFolders,
    settingsData,
    protocolTemplates,
    protocols : {...protocolsCache, getProtocolVersionByType},
    protocolsSchematics: {schematics, addProtocolSchematic, getProtocolSchematicById}
  };
}
